/**
 * PD-16936 — the per-tenant maximum length of an order's Customer PO.
 *
 * Some ERPs refuse a PO longer than they can store (NetSuite, for
 * Pharmsource, rejects anything over 45 characters), and the limit differs
 * by ERP and tenant, so it is the `ordering.customer_po_max_length` instance
 * setting rather than a constant. The CRM fields, the storefront checkout and
 * crm-api all judge a value with this one rule, so the counter a user sees
 * and the refusal the server sends always agree.
 *
 * A too-long PO is refused, never shortened: a truncated PO would reach the
 * customer's accounts payable as a different reference than the one they
 * gave.
 */

/**
 * The `order.customerPO` column is `varchar(255)`, so no tenant limit can be
 * higher, and the CRM fields and DTOs cap at it when no limit is set.
 */
export const CUSTOMER_PO_COLUMN_MAX_LENGTH = 255;

/**
 * The length of a PO as the database and the ERP count it: in characters
 * (code points). `String.length` counts UTF-16 units, so one emoji would read
 * as two and the counter would disagree with the column.
 */
export function customerPoLength(value: string | null | undefined): number {
	if (value == null) {
		return 0;
	}

	return Array.from(value).length;
}

/**
 * The refusal for a PO over `maxLength`, or null when it may be saved.
 *
 * Null when the tenant has no limit, when the PO is empty, or when it is
 * unchanged from `previousValue`. The last case matters: a PO saved before
 * the tenant set its limit (or written by the legacy CRM, which this check
 * does not cover) must not stop someone editing an unrelated field on the
 * same order. Placing an order checks the stored PO separately, because the
 * ERP refuses a long one whenever it arrives.
 */
export function getCustomerPoLengthError(
	value: string | null | undefined,
	maxLength: number | null | undefined,
	previousValue?: string | null,
): string | null {
	if (maxLength == null || value == null || value === '') {
		return null;
	}

	if (previousValue != null && value === previousValue) {
		return null;
	}

	if (customerPoLength(value) <= maxLength) {
		return null;
	}

	return `Customer PO cannot exceed ${maxLength} characters.`;
}
